/**
 * Centralized fetch for /api/* with credentials and consistent JSON/401 handling.
 * Prevents domain/cookie mismatch: always sends cookies (credentials: "include").
 * Use for any client call that expects session-auth; 401 returns typed error for sign-in UX.
 */

export type FetchJsonResult<T = unknown> =
  | { ok: true; data: T; status: number }
  | { ok: false; status: number; data: unknown; errorCode?: string; message?: string }

export class UnauthenticatedError extends Error {
  readonly status = 401
  readonly errorCode = 'UNAUTHENTICATED'
  constructor(message = 'Sign in required.') {
    super(message)
    this.name = 'UnauthenticatedError'
  }
}

/**
 * Fetch URL (same-origin /api/*), parse JSON, send cookies.
 * On 401 with { error: "UNAUTHENTICATED" }, returns ok:false with errorCode so UI can trigger sign-in.
 * Does not throw on 401; returns result so caller can handle cleanly (no React crash).
 */
export async function fetchJson<T = unknown>(
  url: string,
  options: RequestInit = {}
): Promise<FetchJsonResult<T>> {
  const { method = 'GET', headers = {}, body, ...rest } = options
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(body && typeof body === 'string' ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    body,
    ...rest,
  })

  let data: unknown
  try {
    const text = await res.text()
    data = text ? JSON.parse(text) : null
  } catch {
    data = null
  }

  if (res.ok) {
    return { ok: true, data: data as T, status: res.status }
  }

  const errPayload = data as { error?: string; message?: string } | null
  const isUnauth = res.status === 401 || errPayload?.error === 'UNAUTHENTICATED'
  return {
    ok: false,
    status: res.status,
    data,
    errorCode: isUnauth ? 'UNAUTHENTICATED' : errPayload?.error,
    message: errPayload?.message,
  }
}

/**
 * Like fetchJson but throws UnauthenticatedError on 401 so callers can catch and open sign-in modal.
 * Use when you want throw-based flow; otherwise use fetchJson and check result.ok / result.errorCode.
 */
export async function fetchJsonOrThrow<T = unknown>(
  url: string,
  options: RequestInit = {}
): Promise<{ data: T; status: number }> {
  const result = await fetchJson<T>(url, options)
  if (result.ok) return { data: result.data, status: result.status }
  if (result.status === 401 || result.errorCode === 'UNAUTHENTICATED') {
    throw new UnauthenticatedError(result.message)
  }
  throw new Error(result.message || `Request failed with status ${result.status}`)
}
